import {
  AGENT_GATEWAY_ERRORS,
  type ApprovalPolicy,
  createSessionResponseSchema,
  type runTurnBodySchema,
  sessionsPath,
  TURN_ID_HEADER,
  turnActionPath,
  turnsPath,
} from '@salescore/agent-contract'
import { Effect } from 'effect'
import type { z } from 'zod'

import { isPendingId } from '../domain/pendingId'
import { chatEventsReceived, turnIdAssigned, turnReset, turnStopFailed, turnStreamClosed } from '../state/agentChat'
import { targetViewNotFound } from '../state/agentTargets'
import { sessionIdAssigned } from '../state/pendingId'
import { type AgentApi, type AgentApiError, parseJsonResponse } from './agentApi'
import { receiveRunTurnEvents, resumeTurnFromStorage } from './turnStream'

// 指示の送信と中断。処理の流れは salescore-agent-docs の arch/frontend/specification.md の「指示の送信と応答」にある

export interface SendPromptDependencies {
  api: AgentApi
  // ウィジェットの状態（agentWidgetState.ts）の dispatch
  dispatch: (action: AgentChatSendAction) => void
  // turnId が届く前に中断を押されていたかを返し、覚えていた要求を消す（useAgentChat の cancelRequests）
  takeCancelRequest: (pendingTurnId: string) => boolean
  // turnId が届いてから送った中断が失敗した
  notifyCancelFailed: () => void
  // API が指示を受け付けなかったときに、断られたチャットを渡す。呼び出し側が、そのチャットを開いていれば、書いた指示を入力欄に戻す。
  // 新しいチャットなら、セッションを作った後は作ったセッションの ID を渡す（開いているチャットも、その ID に付け替わっているため）
  restorePrompt: (agentSessionId: string) => void
  // 表示する文言（i18n を通した値）
  messages: { sendFailed: string; disconnected: string }
}

// 指示の送信の流れが dispatch する action
type AgentChatSendAction = ReturnType<
  | typeof chatEventsReceived
  | typeof sessionIdAssigned
  | typeof targetViewNotFound
  | typeof turnIdAssigned
  | typeof turnStopFailed
  | typeof turnReset
  | typeof turnStreamClosed
>

export interface SendPromptRequest {
  // 送るチャット。新しいチャットなら仮の ID で、最初にセッションを作る
  sessionKey: string
  // 表示に使っている仮の turnId（turnStarted で足した行）
  pendingTurnId: string
  prompt: string
  // 入力欄の下で選んだ承認モード（approvalMode.ts）。送る時点の値を渡す
  approvalPolicy: ApprovalPolicy
  // そのチャットの会話の対象の view の id（agentTargets.ts）。対象がなければ null
  targetViewId: string | null
}

/**
 * 実行中のターンを中断する。turnId は API が振ったもの（仮の turnId では送れない）。
 */
const requestTurnCancel = (
  api: AgentApi,
  target: { agentSessionId: string; turnId: string },
): Effect.Effect<void, AgentApiError> =>
  // 応答の本文（受け付けたか、受け付けなかった理由）は読まない。受け付けなかったとき（すでに終わっていた、見つからない）も、
  // ターンの終わりが配信で届くので、画面ですることがない
  api(turnActionPath(target, 'cancel'), { method: 'POST' }).pipe(Effect.asVoid)

/**
 * 実行中のターンに中断を送る。送れなかったら、ターンは続いているので、停止のボタンに戻して知らせ、もう一度押せるようにする。
 * 停止を押したのが turnId の届く前（届いた時点で送る）でも後でも、同じ扱いにするため 1 つにまとめる
 */
export const cancelTurnOrRestoreStop = (
  dependencies: Pick<SendPromptDependencies, 'api' | 'dispatch' | 'notifyCancelFailed'>,
  target: { agentSessionId: string; turnId: string },
): Effect.Effect<void> =>
  requestTurnCancel(dependencies.api, target).pipe(
    Effect.catchAll(() =>
      Effect.sync(() => {
        dependencies.dispatch(turnStopFailed(target))
        dependencies.notifyCancelFailed()
      }),
    ),
  )

/**
 * 指示を送り、応答の SSE を受けて会話に映す。新しいチャットなら、先にセッションを作る（runtime はセッションを作らない）。
 * API が指示を受け付けなかったときは、理由を指示の下に出し、断られたチャットを restorePrompt に渡す。
 * 受け付けた後に配信が切れたら、storage の配信でつなぎ直し、それでも終わりを受けられなければ、切れたことを出す
 */
export const sendPromptToAgent = (
  dependencies: SendPromptDependencies,
  { sessionKey, pendingTurnId, prompt, approvalPolicy, targetViewId }: SendPromptRequest,
): Effect.Effect<void> => {
  const { api, dispatch, messages } = dependencies
  const closeTurn = (agentSessionId: string, turnId: string, message: string): Effect.Effect<void> =>
    Effect.sync(() => {
      dispatch(turnStreamClosed({ agentSessionId, turnId, message }))
    })
  // BE の文言（AGENT_GATEWAY_ERRORS）は利用者向けなので、あればそのまま出す
  const rejectPrompt =
    (agentSessionId: string) =>
    (error: AgentApiError): Effect.Effect<void> =>
      Effect.sync(() => {
        dependencies.takeCancelRequest(pendingTurnId)
        if (error.body?.code === AGENT_GATEWAY_ERRORS.viewNotFound.code) {
          dispatch(targetViewNotFound({ agentSessionId }))
        }
        dependencies.restorePrompt(agentSessionId)
      }).pipe(Effect.zipRight(closeTurn(agentSessionId, pendingTurnId, error.body?.message ?? messages.sendFailed)))

  const runTurn = (agentSessionId: string): Effect.Effect<void> =>
    Effect.gen(function* () {
      const response = yield* api(turnsPath({ agentSessionId }), {
        method: 'POST',
        // 項目名を取り違えたら型の検査で止まるよう、BE が検証する本文のスキーマの型に合わせる
        body: JSON.stringify({
          prompt,
          approvalPolicy,
          page: targetViewId === null ? null : { id: targetViewId },
        } satisfies z.input<typeof runTurnBodySchema>),
      })
      // BE は振った turnId を X-Turn-Id で返す。画面と API は別の origin なので、BE がこの応答で Access-Control-Expose-Headers に入れて公開している
      const turnId = response.headers.get(TURN_ID_HEADER) ?? pendingTurnId
      if (turnId !== pendingTurnId) {
        dispatch(turnIdAssigned({ agentSessionId, pendingTurnId, turnId }))
        // turnId が届く前に押された中断を、届いた turnId で送る。結果は配信のターンの終わりで届く
        if (dependencies.takeCancelRequest(pendingTurnId)) {
          yield* Effect.forkDaemon(cancelTurnOrRestoreStop(dependencies, { agentSessionId, turnId }))
        }
      }

      const isEnded = yield* receiveRunTurnEvents(response, (events) => {
        dispatch(chatEventsReceived({ agentSessionId, turnId, events }))
      })
      // turnId が分からないままなら、つなぎ直せない
      if (!isEnded && !isPendingId(turnId)) {
        yield* resumeTurnFromStorage(
          api,
          { agentSessionId, turnId },
          {
            reset: () => {
              dispatch(turnReset({ agentSessionId, turnId }))
            },
            receive: (events) => {
              dispatch(chatEventsReceived({ agentSessionId, turnId, events }))
            },
            fail: (message) => {
              dispatch(turnStreamClosed({ agentSessionId, turnId, message }))
            },
          },
        )
      }
      // storage からもターンの終わりを受けられなかったら、実行中を解く。終わりを受けていれば何もしない
      yield* closeTurn(agentSessionId, turnId, messages.disconnected)
    }).pipe(Effect.catchAll(rejectPrompt(agentSessionId)))

  if (!isPendingId(sessionKey)) {
    return runTurn(sessionKey)
  }
  return api(sessionsPath(), { method: 'POST' }).pipe(
    Effect.flatMap((response) => parseJsonResponse(response, createSessionResponseSchema)),
    Effect.matchEffect({
      onFailure: rejectPrompt(sessionKey),
      onSuccess: ({ agentSession }) =>
        // 仮の ID で出していた会話と開いているチャットを、作ったセッションに移す
        Effect.sync(() => {
          dispatch(sessionIdAssigned({ pendingSessionId: sessionKey, agentSessionId: agentSession.id }))
        }).pipe(Effect.zipRight(runTurn(agentSession.id))),
    }),
  )
}
